import { css } from '@emotion/react'
import {
  Button,
  ButtonGroup,
  ButtonProps,
  HFlow,
  Modal,
  ModalBody,
  ModalFooter,
  ModalFooterButton,
  ModalHeader,
  Tooltip,
  VFlow,
} from 'bold-ui'
import { useSession } from 'components/auth/useSession'
import { useAtendimentoContext } from 'hooks/atendimento-context/useAtendimentoContext'
import { useConfiguracoes } from 'hooks/configuracoes'
import { useFirebase } from 'hooks/firebase/useFirebase'
import { useCallback, useState } from 'react'
import { useHistory } from 'react-router'
import { isHttpsConnection } from 'util/https'
import { CidadaoFolhaRosto } from 'view/atendimentos/detail/folha-rosto/model'
import { HTTPS_ASSINATURA_DIGITAL_MESSAGE } from 'view/configuracoes/instalacao/conexao/internet/assinatura-digital/model-assinaturaDigital'

import { MedicamentoFormModel } from '../model-prescricao'
import { ImpressaoPrescricaoView } from './impressao/ImpressaoPrescricaoView'
import { GerarPrescricaoDigitalView } from './prescricao-digital/GerarPrescricaoDigitalView'
import { PrescricaoDigitalFormModel } from './prescricao-digital/model-prescricaoDigital'

enum ActiveTab {
  IMPRIMIR,
  PRESCRICAO_DIGITAL,
}

export interface EncaminharPrescricaoModalProps {
  atendimentoId: ID
  medicamentos: MedicamentoFormModel[]
  prescricaoDigital?: PrescricaoDigitalFormModel
  isReimpressao: boolean
  cidadaoFolhaRosto?: CidadaoFolhaRosto
  updatePrescricaoDigital?: (prescricao: PrescricaoDigitalFormModel) => void
}

export interface EncaminhamentoPrescricaoPrimaryButtonConfig
  extends Pick<ButtonProps, 'loading' | 'disabled' | 'onClick'> {
  text: string
  tooltipText: string
}

export default function EncaminharPrescricaoModal(props: EncaminharPrescricaoModalProps) {
  const {
    medicamentos,
    atendimentoId,
    prescricaoDigital,
    isReimpressao,
    cidadaoFolhaRosto,
    updatePrescricaoDigital,
  } = props

  const { analytics } = useFirebase()

  const [activeTab, setActiveTab] = useState<ActiveTab>(ActiveTab.IMPRIMIR)
  const [primaryButtonConfig, setPrimaryButtonConfig] = useState<EncaminhamentoPrescricaoPrimaryButtonConfig>({
    text: 'Imprimir',
    tooltipText: '',
  })

  const {
    data: {
      acesso: {
        municipio: { id: municipioId },
      },
    },
  } = useSession()

  const {
    configuracoes: { isPrescricaoDigitalEnabled },
  } = useConfiguracoes()

  const {
    cidadao: {
      id: cidadaoId = null,
      dataNascimento: cidadaoDataNascimento = null,
      sexo: cidadaoSexo = null,
      identidadeGeneroDbEnum: cidadaoIdentidadeGenero = null,
    } = {},
  } = useAtendimentoContext() || {}

  const history = useHistory()
  const handleModalClose = useCallback(() => history.goBack(), [history])
  const handleClickPrescricaoTab = () => {
    analytics.logEvent('click_aba_prescricao_digital')
    setActiveTab(ActiveTab.PRESCRICAO_DIGITAL)
  }

  const encaminhamentoAction = isReimpressao ? 'imprimir' : 'encaminhar'

  return (
    <Modal open onClose={handleModalClose} closeOnBackdropClick={false} style={styles.mainContainer}>
      <ModalHeader
        title={`${encaminhamentoAction.capitalize()} prescrição`}
        subtitle={`Selecione os medicamentos que deseja ${encaminhamentoAction}`}
      />
      <ModalBody>
        <VFlow>
          <VFlow vSpacing={0.5}>
            {!isReimpressao && (
              <ButtonGroup>
                <Button
                  size='small'
                  kind={activeTab === ActiveTab.IMPRIMIR ? 'primary' : 'normal'}
                  onClick={() => setActiveTab(ActiveTab.IMPRIMIR)}
                >
                  Imprimir
                </Button>
                <Tooltip
                  text={
                    !isPrescricaoDigitalEnabled
                      ? 'Assinatura digital não habilitada na sua instalação, comunique o administrador da sua instalação para ativá-la.'
                      : !isHttpsConnection && HTTPS_ASSINATURA_DIGITAL_MESSAGE
                  }
                >
                  <Button
                    size='small'
                    disabled={!isPrescricaoDigitalEnabled || !isHttpsConnection}
                    kind={activeTab === ActiveTab.PRESCRICAO_DIGITAL ? 'primary' : 'normal'}
                    onClick={handleClickPrescricaoTab}
                  >
                    Prescrição digital
                  </Button>
                </Tooltip>
              </ButtonGroup>
            )}
          </VFlow>
          {activeTab === ActiveTab.IMPRIMIR && (
            <ImpressaoPrescricaoView
              medicamentos={medicamentos}
              prescricaoDigital={prescricaoDigital}
              atendimentoId={atendimentoId}
              cidadaoId={cidadaoId || cidadaoFolhaRosto?.id}
              municipioId={municipioId}
              cidadaoDataNascimento={cidadaoDataNascimento || cidadaoFolhaRosto?.dataNascimento}
              cidadaoSexo={cidadaoSexo || cidadaoFolhaRosto?.sexo}
              cidadaoIdentidadeGenero={cidadaoIdentidadeGenero || cidadaoFolhaRosto?.identidadeGeneroDbEnum}
              isReimpressao={isReimpressao}
              setPrimaryButtonConfig={setPrimaryButtonConfig}
            />
          )}
          {activeTab === ActiveTab.PRESCRICAO_DIGITAL && (
            <GerarPrescricaoDigitalView
              prescricaoDigital={prescricaoDigital}
              updatePrescricaoDigital={updatePrescricaoDigital}
              atendimentoId={atendimentoId}
              municipioId={municipioId}
              cidadaoId={cidadaoId || cidadaoFolhaRosto?.id}
              cidadaoDataNascimento={cidadaoDataNascimento || cidadaoFolhaRosto?.dataNascimento}
              cidadaoSexo={cidadaoSexo || cidadaoFolhaRosto?.sexo}
              setPrimaryButtonConfig={setPrimaryButtonConfig}
            />
          )}
        </VFlow>
      </ModalBody>
      <ModalFooter>
        <HFlow justifyContent='flex-end'>
          <ModalFooterButton onClick={handleModalClose} data-cy='CancelarImprimirButton'>
            Cancelar
          </ModalFooterButton>

          <Tooltip text={primaryButtonConfig.tooltipText}>
            <ModalFooterButton
              kind='primary'
              onClick={primaryButtonConfig.onClick}
              disabled={primaryButtonConfig.disabled}
              loading={primaryButtonConfig.loading}
              data-cy='ImprimirPrescricaoButton'
            >
              {primaryButtonConfig.text}
            </ModalFooterButton>
          </Tooltip>
        </HFlow>
      </ModalFooter>
    </Modal>
  )
}

const styles = {
  mainContainer: css`
    width: 42.5rem;
    height: auto;
  `,
}
